import { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { JoystickShape } from 'react-joystick-component';
import { FormikField } from '~/components/common/FormikField';
import SignalJoystick from './Joystick';
import { Well } from '~/components/common/Well';
import { Alert } from '~/components/ui/alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

type JoystickDirection = 'FORWARD' | 'RIGHT' | 'LEFT' | 'BACKWARD' | 'CENTER';

export interface IJoystickUpdateEvent {
  type: 'move' | 'stop' | 'start' | 'stream';
  x: number | null;
  y: number | null;
  direction: JoystickDirection | null;
  distance: number | null; // Percentile 0-100% of joystick
}

type FormValues = {
  latitude: number;
  longitude: number;
  height: number;
  heading: number;
  pitch: number;
  roll: number;
};

type PlacementFieldsProps = {
  placement: FormValues;
  zoomValue: number;
};

export default function VomPlacementFields({
  placement,
  zoomValue = 1,
}: PlacementFieldsProps) {
  const { setFieldValue, values } = useFormikContext<FormValues>();
  const [joystickSpeed, setJoystickSpeed] = useState<number>(0.0001);

  useEffect(() => {
    setFieldValue('latitude', placement.latitude);
    setFieldValue('longitude', placement.longitude);
    setFieldValue('height', placement.height);
    setFieldValue('heading', placement.heading);
    setFieldValue('pitch', placement.pitch);
    setFieldValue('roll', placement.roll);
  }, [placement, setFieldValue]);

  useEffect(() => {
    setJoystickSpeed(zoomValue * 0.0001);
  }, [zoomValue]);

  const handleMove = (event: IJoystickUpdateEvent | null) => {
    if (!event) {
      return;
    }
    if (event.type === 'move' && event.x && event.y) {
      const newPlacement = values;
      newPlacement.latitude =
        Number(newPlacement.latitude) + event.y * joystickSpeed;
      newPlacement.longitude =
        Number(newPlacement.longitude) + event.x * joystickSpeed;
      setFieldValue('latitude', newPlacement.latitude.toFixed(5));
      setFieldValue('longitude', newPlacement.longitude.toFixed(5));
    }
  };

  const handleHeightMove = (event: IJoystickUpdateEvent | null) => {
    if (!event) {
      return;
    }
    if (event.type === 'move' && event.y) {
      const newPlacement = values;
      newPlacement.height = Number(newPlacement.height) + event.y;
      setFieldValue('height', newPlacement.height.toFixed(5));
    }
  };

  return (
    <div className="space-y-2">
      <Alert>
        <FontAwesomeIcon icon={faInfoCircle} />
        <div>
          Use <span className="kbd">opt</span>/<span className="kbd">alt</span>{' '}
          + click to place model
        </div>
      </Alert>

      <div className="grid lg:grid-cols-2 gap-4">
        <Well className="space-y-2">
          <div className="flex justify-center">
            <SignalJoystick
              size={70}
              signalRate={50}
              callback={handleMove}
              throttle={50}
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-2">
            <Field
              name="latitude"
              label="Latitude"
              component={FormikField}
              type="number"
              step={0.0001}
              placeholder="Latitude"
            />
            <Field
              name="longitude"
              label="Longitude"
              component={FormikField}
              type="number"
              step={0.0001}
              placeholder="Longitude"
            />
          </div>
        </Well>

        <Well className="space-y-2">
          <div className="flex justify-center">
            <SignalJoystick
              size={70}
              signalRate={50}
              callback={handleHeightMove}
              stickShape={JoystickShape.Square}
              baseShape={JoystickShape.Square}
              joystickdirection="AxisY"
              stickColor="#3d59ac"
              stickImage="/assets/images/up-down-caret-white.png"
              throttle={50}
            />
          </div>

          <Field
            name="height"
            label="Height"
            component={FormikField}
            type="number"
            placeholder="height"
          />
        </Well>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Field
          name="heading"
          label="Heading"
          component={FormikField}
          type="number"
          placeholder="Heading"
          showSpin={true}
        />
        <Field
          name="pitch"
          label="Pitch"
          component={FormikField}
          type="number"
          placeholder="Pitch"
          showSpin={true}
        />
        <Field
          name="roll"
          label="Roll"
          component={FormikField}
          type="number"
          placeholder="Roll"
          showSpin={true}
        />
      </div>
    </div>
  );
}
